/**
 * WorkflowCenterBar — the "ora  Work Simplified" brand bar (Joachim, 2026-10-21: it read
 * "oraWorkflows" until then; the add-in's group is "Work Simplified" too).
 *
 * ONE component for the top of the Outlook pane AND every full-frame takeover
 * (run drawer, launch form, More information). The pane used the ora logo
 * image while the takeovers spelled "ora" in text with a sunrise dot, so the
 * firefly turned into a dot whenever the Servicer opened something (story
 * 28633). Both now render this.
 *
 * `onBack` puts "← Back" at the left edge (story 28633: the back row moved
 * into the header to save a row on every page). It is the takeover's ONLY
 * exit, so it is always visible text, never an icon alone. `backAriaLabel`
 * keeps the destination ("Back to your queue") for screen readers, since the
 * visible label no longer names it.
 *
 * The logo is a prop because the image lives in the host's static assets
 * (the add-in serves it under its own base path).
 *
 * Shared — no Office.js, no Remix import.
 */

import CustomTooltip from "~/common/components/CustomTooltip";
import ArrowLeft from "~/common/components/svgs/ArrowLeft";

export const WORKFLOW_CENTER_LABEL = "Work Simplified";

export interface WorkflowCenterBarProps {
  /** URL of the ora logo (wordmark with the firefly). */
  logoSrc: string;
  /** Omitted → no back control (the pane's own home bar). */
  onBack?: () => void;
  /** Accessible name for the back control; defaults to "Back". */
  backAriaLabel?: string;
  backTestId?: string;
  testId?: string;
  /** The words after the mark. Omitted → "Work Simplified". Either way the label is a phrase
   *  of its own, a word apart from the mark ("ora  Work Simplified", the v4 header). */
  label?: string;
}

export function WorkflowCenterBar({
  logoSrc,
  onBack,
  backAriaLabel,
  backTestId = "takeover-back",
  testId = "workflow-center-bar",
  label,
}: WorkflowCenterBarProps) {
  return (
    <div
      className="relative flex h-[44px] flex-shrink-0 items-center justify-center border-b border-primary-gray-light-200 bg-base-white"
      data-testid={testId}
    >
      {onBack ? (
        // ora's back pattern (WorkflowUploadShell / BackToGoBeyondButton): the
        // ArrowLeft icon, centred on the label. The "←" glyph came from a
        // fallback font and sat off the text's centre line. The destination
        // ("Back to your queue") is the accessible name and the ora tooltip.
        <span className="absolute left-2 top-1/2 -translate-y-1/2">
          <CustomTooltip title={backAriaLabel ?? "Back"} placement="bottom-start" arrow isOnModal>
            <button
              type="button"
              onClick={onBack}
              aria-label={backAriaLabel ?? "Back"}
              data-testid={backTestId}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs leading-none text-primary-gray-light-700 hover:bg-primary-gray-light-50 hover:text-primary-gray-light-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise"
            >
              <ArrowLeft className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Back</span>
            </button>
          </CustomTooltip>
        </span>
      ) : null}
      {/* The brand lockup: logo image, then the label ("Work Simplified").
          Baseline-aligned: an image's baseline is its bottom edge, and the
          PNG's "ora" sits 2.2px above that edge (10% transparent padding at
          22px), so the image is nudged down 2px to put the "ora" letters on
          the same baseline as the label. Light weight; x-height matched to
          "ora". ml-1 plus the PNG's own right padding leaves a small gap after
          the firefly (Joachim, 2026-10-21: tighter than the prototype's 13px). */}
      <span
        className="flex items-baseline text-[18px] font-light leading-none tracking-[-0.01em] text-primary-gray-light-800"
        data-testid="workflow-center-brand"
      >
        <img
          src={logoSrc}
          alt="ora"
          className="h-[22px] translate-y-[2px] object-contain"
        />
        <span className="ml-1" data-testid="workflow-center-label">
          {label ?? WORKFLOW_CENTER_LABEL}
        </span>
      </span>
    </div>
  );
}

export default WorkflowCenterBar;
